import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, UserPlus } from "lucide-react";
import { PERSON_OPTIONS, PERSON_LABEL, emptyContact, describeContact } from "@/lib/personOptions";

const EMPTY = { name: "", address: "", city: "", state: "", zip: "", county: "", phone: "", hours: "", notes: "" };
const DESC_KEYS = ["person_sex", "person_age", "person_ethnicity", "person_height", "person_weight", "person_hair", "person_eyes", "person_other"];

function ContactRow({ c, idx, onChange, onRemove }) {
  const set = (k) => (e) => onChange({ ...c, [k]: e.target.value });
  return (
    <div className="rounded-md border border-slate-200 bg-white p-2.5 space-y-2" data-testid={`agent-contact-${idx}`}>
      <div className="flex items-center gap-2">
        <Input className="h-8 flex-1" placeholder="Employee name *" value={c.person_name} onChange={set("person_name")} data-testid={`agent-contact-name-${idx}`} />
        <Input className="h-8 flex-1" placeholder="Title (e.g. Intake Clerk)" value={c.person_title} onChange={set("person_title")} data-testid={`agent-contact-title-${idx}`} />
        <button type="button" onClick={onRemove} className="text-rose-600 hover:bg-rose-50 rounded p-1.5" title="Remove" data-testid={`agent-contact-remove-${idx}`}><Trash2 className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
        {DESC_KEYS.map((k) => (
          <select key={k} value={c[k] || ""} onChange={set(k)} className="h-8 rounded border border-slate-300 bg-white px-1.5 text-xs" data-testid={`agent-contact-${k}-${idx}`}>
            <option value="">{PERSON_LABEL[k]}</option>
            {PERSON_OPTIONS[k].map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        ))}
        {c.person_other === "Other" && <Input className="h-8 text-xs sm:col-span-4" placeholder="Describe (e.g. red lanyard, limp)" value={c.person_other_text} onChange={set("person_other_text")} data-testid={`agent-contact-other-${idx}`} />}
      </div>
      {describeContact(c) && <div className="text-[11px] text-slate-500">Will fill as: <span className="text-slate-700">{c.person_name}{describeContact(c) ? ` — ${describeContact(c)}` : ""}</span></div>}
    </div>
  );
}

export function RegisteredAgentDialog({ agent, onClose, onSaved }) {
  const isEdit = Boolean(agent?.id);
  const [f, setF] = useState({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, agent?.[k] || ""])) });
  const [contacts, setContacts] = useState(() => (agent?.contacts?.length ? agent.contacts.map((c) => ({ ...emptyContact(), ...c })) : []));
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const lp = agent?.last_person;
  const canAddLast = lp?.person_name && !contacts.some((c) => c.person_name.trim().toLowerCase() === lp.person_name.trim().toLowerCase());
  const addContact = (seed) => setContacts((p) => [...p, { ...emptyContact(), ...(seed || {}) }]);

  const save = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error("Registered agent name is required");
    const cleaned = contacts.filter((c) => c.person_name.trim());
    setSaving(true);
    try {
      const body = { ...f, contacts: cleaned, contact_name: "", contact_title: "", contact_description: "" };
      if (isEdit) { await api.patch(`/registered-agents/${agent.id}`, body); toast.success("Registered agent updated"); }
      else { await api.post("/registered-agents", body); toast.success("Registered agent added"); }
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="agent-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Edit Registered Agent" : "New Registered Agent"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div><Label className="sm-label">Company name *</Label><Input data-testid="agent-name" value={f.name} onChange={set("name")} placeholder="e.g. CT Corporation System" /></div>
          <div><Label className="sm-label">Street address</Label><Input data-testid="agent-address" value={f.address} onChange={set("address")} /></div>
          <div className="grid grid-cols-3 gap-2">
            <Input data-testid="agent-city" placeholder="City" value={f.city} onChange={set("city")} />
            <Input data-testid="agent-state" placeholder="State" value={f.state} onChange={set("state")} />
            <Input data-testid="agent-zip" placeholder="Zip" value={f.zip} onChange={set("zip")} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="agent-county" placeholder="County" value={f.county} onChange={set("county")} />
            <Input data-testid="agent-phone" placeholder="Phone" value={f.phone} onChange={set("phone")} />
          </div>
          <div><Label className="sm-label">Hours</Label><Input data-testid="agent-hours" placeholder="e.g. Mon–Fri 9:00 AM – 5:00 PM, closed 12–1" value={f.hours} onChange={set("hours")} /></div>

          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div><Label className="sm-label">Employees who accept service</Label><div className="text-[11px] text-slate-500">Each one becomes a one-tap fill in the attempt dialog — name, title and description go straight to the affidavit.</div></div>
              <div className="flex gap-2">
                {canAddLast && <Button type="button" variant="outline" size="sm" onClick={() => addContact(lp)} data-testid="agent-add-last"><UserPlus className="h-3.5 w-3.5 mr-1" />Add last served: {lp.person_name}</Button>}
                <Button type="button" variant="outline" size="sm" onClick={() => addContact()} data-testid="agent-add-contact"><Plus className="h-3.5 w-3.5 mr-1" />Add employee</Button>
              </div>
            </div>
            {contacts.length === 0 && <div className="text-xs text-slate-500 italic">No employees yet — add the intake clerk, receptionist, or whoever usually signs for documents.</div>}
            <div className="space-y-2">
              {contacts.map((c, i) => <ContactRow key={i} idx={i} c={c} onChange={(nc) => setContacts((p) => p.map((x, j) => (j === i ? nc : x)))} onRemove={() => setContacts((p) => p.filter((_, j) => j !== i))} />)}
            </div>
          </div>

          <Textarea data-testid="agent-notes" rows={2} placeholder="Notes (suite number, parking, check-in desk, ID required…)" value={f.notes} onChange={set("notes")} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="agent-cancel">Cancel</Button>
            <Button type="submit" className="sm-green" disabled={saving} data-testid="agent-save">{saving ? "Saving…" : isEdit ? "Save changes" : "Add agent"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
